/* One-click Mock Recall / Traceability Event Report. */
import React, { useState } from 'react';
import { recallReport, createTraceEvent, updateTraceEvent } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, Loader2, AlertTriangle, User, Truck, FlaskConical, PackageOpen, Boxes, Save, Plus, Trash2 } from 'lucide-react';
import ReportLayout, { exportCsv } from './ReportLayout';
import CustomerDraftsPanel from './CustomerDraftsPanel';

const TYPES = [
  { value: 'supplier_lot', label: 'Supplier lot number', placeholder: 'e.g. MA4001-26A007' },
  { value: 'milk_intake_ref', label: 'Milk intake reference', placeholder: 'e.g. MI-2026-00001' },
  { value: 'make_day_ref', label: 'Make Day reference', placeholder: 'e.g. MD-2026-00001' },
  { value: 'cheese_batch_number', label: 'Cheese batch number', placeholder: 'e.g. CB-2026-014' },
];

const EVENT_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-zinc-200 text-zinc-700' },
  { value: 'under_review', label: 'Under review', color: 'bg-amber-100 text-amber-800' },
  { value: 'approved', label: 'Approved internally', color: 'bg-blue-100 text-blue-800' },
  { value: 'comms_prepared', label: 'Customer comms prepared', color: 'bg-purple-100 text-purple-800' },
  { value: 'closed', label: 'Closed', color: 'bg-emerald-100 text-emerald-800' },
];

export default function MockRecallReport() {
  const [type, setType] = useState('supplier_lot');
  const [val, setVal] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Phase 3: persisted event state
  const [event, setEvent] = useState(null); // saved event { id, event_ref, status, internal_notes, customer_drafts }
  const [eventStatus, setEventStatus] = useState('draft');
  const [notes, setNotes] = useState([]); // [{author, note, created_at}]
  const [newNote, setNewNote] = useState('');
  const [savingEvent, setSavingEvent] = useState(false);
  const [drafts, setDrafts] = useState([]);

  const run = async () => {
    if (!val.trim()) { toast.error('Enter a value'); return; }
    setLoading(true);
    try {
      const r = await recallReport({ [type]: val.trim() });
      setData(r.data);
      // reset event on new run
      setEvent(null); setEventStatus('draft'); setNotes([]); setDrafts([]);
      if (!r.data.cheese_batches?.length && !r.data.make_days?.length) {
        toast.info('No downstream impact found for this input.');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Report failed'); }
    finally { setLoading(false); }
  };

  const cur = TYPES.find(t => t.value === type);
  const s = data?.summary;

  const saveEvent = async () => {
    if (!data) { toast.error('Run a report first'); return; }
    setSavingEvent(true);
    try {
      const payload = {
        starting: s?.starting || '—',
        starting_type: type,
        starting_value: val,
        summary_snapshot: s || {},
        affected_customers: data.affected_customers || [],
        status: eventStatus,
        internal_notes: notes.map(n => ({ author: n.author, note: n.note, created_at: n.created_at })),
        customer_drafts: drafts || [],
      };
      if (event?.id) {
        const r = await updateTraceEvent(event.id, payload);
        setEvent(r.data);
        toast.success(`Event ${r.data.event_ref} saved`);
      } else {
        const r = await createTraceEvent(payload);
        setEvent(r.data);
        toast.success(`Event ${r.data.event_ref} created`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingEvent(false); }
  };

  const addNote = () => {
    if (!newNote.trim()) return;
    setNotes(arr => [...arr, { note: newNote.trim(), created_at: new Date().toISOString() }]);
    setNewNote('');
  };
  const removeNote = (i) => setNotes(arr => arr.filter((_, idx) => idx !== i));

  const csvDispatchRows = () => {
    if (!data) return;
    // Pick the best dataset for export depending on what the trace returned
    if (data.downstream?.length) {
      const rows = data.downstream.map(row => ({
        dispatch_ref: row.dispatch?.reference || row.dispatch?.dispatch_ref || row.dispatch?.id,
        dispatch_date: row.dispatch?.dispatch_date || '',
        customer: row.customer?.name || '',
        city: row.customer?.delivery_city || '',
        sales_orders: (row.sales_orders || []).map(so => so.order_number).join('; '),
        cheese_batch: (row.dispatch?.lines || []).map(ln => ln.batch_number).filter(Boolean).join('; '),
        qty: (row.dispatch?.lines || []).reduce((s, ln) => s + (Number(ln.quantity) || 0), 0),
      }));
      exportCsv(`mock-recall-${type}-${val}.csv`, rows, [
        { key: 'dispatch_ref', label: 'Dispatch' },
        { key: 'dispatch_date', label: 'Date' },
        { key: 'customer', label: 'Customer' },
        { key: 'city', label: 'City' },
        { key: 'sales_orders', label: 'Sales orders' },
        { key: 'cheese_batch', label: 'Cheese batches' },
        { key: 'qty', label: 'Qty' },
      ]);
      return;
    }
    // No downstream yet — export affected cheese batches + make-days instead
    const rows = (data.cheese_batches || []).map(b => ({
      batch: b.batch_number || b.batch_code,
      make_day: b.make_day_ref || '',
      use_by: b.use_by_date || '',
      quantity: b.quantity || '',
    }));
    if (!rows.length) {
      toast.info('Nothing to export — no affected batches or dispatches.');
      return;
    }
    exportCsv(`mock-recall-${type}-${val}.csv`, rows, [
      { key: 'batch', label: 'Cheese batch' },
      { key: 'make_day', label: 'Make Day' },
      { key: 'use_by', label: 'Use-by' },
      { key: 'quantity', label: 'Quantity' },
    ]);
  };

  return (
    <div className="space-y-4" data-testid="mock-recall-page">
      <div className="print:hidden">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <h1 className="text-xl font-semibold">Mock Recall · Traceability Event Report</h1>
        </div>
        <p className="text-sm text-muted-foreground">One click. Pick a supplier lot, milk intake, make day or cheese batch and get the full downstream footprint — every make day, batch, dispatch and customer affected. Printable for FSAI / BRCGS audits.</p>
      </div>

      <Card className="print:hidden"><CardContent className="pt-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>Start from</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="recall-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <div className="flex-1">
              <Label>Value</Label>
              <Input value={val} onChange={e => setVal(e.target.value)} placeholder={cur?.placeholder} onKeyDown={e => e.key === 'Enter' && run()} data-testid="recall-value" />
            </div>
            <Button onClick={run} disabled={loading} className="self-end" data-testid="btn-run-recall">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}Generate report
            </Button>
          </div>
        </div>
      </CardContent></Card>

      {data && <ReportLayout
        title="Traceability Event Report"
        subtitle={s?.starting}
        generated_at={s?.generated_at}
        onCsv={csvDispatchRows}
        testid="recall-report"
      >
        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 print:gap-2">
          {[
            { label: 'Make Days affected', value: s.make_days_count, icon: FlaskConical },
            { label: 'Cheese batches', value: s.cheese_batches_count, icon: Boxes },
            { label: 'Dispatches', value: s.dispatches_count, icon: Truck },
            { label: 'Customers affected', value: s.customers_count, icon: User },
            { label: 'Total qty dispatched', value: (s.total_dispatched_quantity || 0).toLocaleString(), icon: PackageOpen },
          ].map((t, i) => (
            <Card key={i} className="print:shadow-none print:border"><CardContent className="pt-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><t.icon className="w-3 h-3" />{t.label}</div>
              <div className="text-2xl font-semibold mt-1">{t.value}</div>
            </CardContent></Card>
          ))}
        </div>

        {/* Affected customers */}
        {data.affected_customers?.length > 0 && <section className="mt-6">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><User className="w-4 h-4" />Affected customers</h3>
          <div className="flex flex-wrap gap-2">
            {data.affected_customers.map((c, i) => (
              <Badge key={i} variant="outline" className="text-sm px-3 py-1.5">
                {c.name}{c.city && <span className="text-muted-foreground ml-2">· {c.city}</span>}
              </Badge>
            ))}
          </div>
        </section>}

        {/* Make days */}
        {data.make_days?.length > 0 && <section className="mt-6">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><FlaskConical className="w-4 h-4" />Make Days affected</h3>
          <table className="w-full text-sm border rounded">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr><th className="text-left px-3 py-2">Ref</th><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Product</th><th className="text-left px-3 py-2">Supervisor</th><th className="text-left px-3 py-2">Status</th></tr></thead>
            <tbody>{data.make_days.map((m, i) => (
              <tr key={i} className="border-t">
                <td className="px-3 py-2 font-mono text-xs">{m.make_day_ref}</td>
                <td className="px-3 py-2">{m.make_date}</td>
                <td className="px-3 py-2">{m.product_type || '—'}</td>
                <td className="px-3 py-2">{m.supervisor || '—'}</td>
                <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{m.status}</Badge></td>
              </tr>))}
            </tbody>
          </table>
        </section>}

        {/* Cheese batches */}
        {data.cheese_batches?.length > 0 && <section className="mt-6">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><Boxes className="w-4 h-4" />Cheese batches produced</h3>
          <table className="w-full text-sm border rounded">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr><th className="text-left px-3 py-2">Batch</th><th className="text-left px-3 py-2">Make Day</th><th className="text-left px-3 py-2">Use-by</th><th className="text-right px-3 py-2">Qty</th></tr></thead>
            <tbody>{data.cheese_batches.map((b, i) => (
              <tr key={i} className="border-t">
                <td className="px-3 py-2 font-mono text-xs">{b.batch_number || b.batch_code}</td>
                <td className="px-3 py-2 font-mono text-xs">{b.make_day_ref || '—'}</td>
                <td className="px-3 py-2">{b.use_by_date || '—'}</td>
                <td className="px-3 py-2 text-right">{b.quantity || '—'}</td>
              </tr>))}
            </tbody>
          </table>
        </section>}

        {/* Downstream dispatches */}
        {data.downstream?.length > 0 && <section className="mt-6">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><Truck className="w-4 h-4" />Downstream dispatches</h3>
          <table className="w-full text-sm border rounded">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr><th className="text-left px-3 py-2">Dispatch</th><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Customer</th><th className="text-left px-3 py-2">City</th><th className="text-left px-3 py-2">Sales orders</th></tr></thead>
            <tbody>{data.downstream.map((row, i) => (
              <tr key={i} className="border-t">
                <td className="px-3 py-2 font-mono text-xs">{row.dispatch?.reference || row.dispatch?.dispatch_ref || row.dispatch?.id?.slice(0, 8)}</td>
                <td className="px-3 py-2">{row.dispatch?.dispatch_date || '—'}</td>
                <td className="px-3 py-2">{row.customer?.name || '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">{row.customer?.delivery_city || '—'}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{(row.sales_orders || []).map(so => so.order_number).join(', ') || '—'}</td>
              </tr>))}
            </tbody>
          </table>
        </section>}

        {!data.make_days?.length && !data.cheese_batches?.length && (
          <div className="text-center text-muted-foreground italic py-8">No downstream impact found.</div>
        )}

        {/* ── Phase 3: Event status + internal notes ───────────────────── */}
        <section className="mt-8" data-testid="event-controls">
          <div className="border-t pt-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4 print:hidden">
              <h3 className="font-semibold">Traceability event</h3>
              <div className="flex items-center gap-2">
                {event?.event_ref && <Badge variant="outline" className="font-mono">{event.event_ref}</Badge>}
                <Button onClick={saveEvent} disabled={savingEvent} data-testid="btn-save-event">
                  {savingEvent && <Loader2 className="w-4 h-4 animate-spin mr-1" />}<Save className="w-4 h-4 mr-1" />{event?.id ? 'Save changes' : 'Save event'}
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={eventStatus} onValueChange={setEventStatus}>
                  <SelectTrigger data-testid="event-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{EVENT_STATUSES.map(st => <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}</SelectContent>
                </Select>
                <div className="mt-2"><Badge className={`${EVENT_STATUSES.find(x => x.value === eventStatus)?.color || ''} border-0`}>{EVENT_STATUSES.find(x => x.value === eventStatus)?.label}</Badge></div>
              </div>

              <div className="md:col-span-2">
                <Label className="text-xs">Internal notes</Label>
                <div className="space-y-2">
                  {notes.length === 0 && <div className="text-xs text-muted-foreground italic py-2">No notes yet. Use this for reason for investigation, who reviewed, action taken, customer contact log, closure notes.</div>}
                  {notes.map((n, i) => (
                    <div key={i} className="text-sm bg-zinc-50 border rounded p-2 flex justify-between gap-2" data-testid={`note-${i}`}>
                      <div>
                        <div className="text-xs text-muted-foreground">{n.author || 'You'} · {n.created_at ? new Date(n.created_at).toLocaleString() : '—'}</div>
                        <div className="whitespace-pre-wrap">{n.note}</div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeNote(i)} className="print:hidden"><Trash2 className="w-3 h-3 text-red-600" /></Button>
                    </div>
                  ))}
                  <div className="flex gap-2 print:hidden">
                    <Textarea rows={2} value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add an internal note (visible to your team only)…" className="flex-1 text-sm" data-testid="new-note" />
                    <Button onClick={addNote} variant="outline" size="sm" data-testid="btn-add-note" className="self-end"><Plus className="w-4 h-4 mr-1" />Add note</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Phase 3: Customer Communication Drafts ─────────────────── */}
        <CustomerDraftsPanel
          recallData={data}
          eventRef={event?.event_ref || ''}
          onChange={setDrafts}
        />
      </ReportLayout>}
    </div>
  );
}
